import { useEffect, useState } from "react";
import { Loader2, Clock, CheckCircle2, XCircle, X, ChevronDown, ChevronUp } from "lucide-react";
import { subscribeUploads, clearFinished } from "@/lib/uploadStatus";

const META = {
  uploading: { label: "Uploading", Icon: Loader2, cls: "text-blue-300", spin: true },
  waiting: { label: "Waiting", Icon: Clock, cls: "text-amber-300" },
  done: { label: "Done", Icon: CheckCircle2, cls: "text-emerald-400" },
  failed: { label: "Failed", Icon: XCircle, cls: "text-red-400" },
};

const Row = ({ it }) => {
  const m = META[it.status];
  return (
    <li className="flex items-start gap-2 py-1.5 border-b border-slate-800 last:border-0" data-testid={`upload-status-row-${it.id}`}>
      <m.Icon className={`w-4 h-4 mt-0.5 shrink-0 ${m.cls} ${m.spin ? "animate-spin" : ""}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-slate-200 truncate" title={it.name}>{it.label ? `${it.label} · ` : ""}{it.name}</div>
        {it.message && <div className="text-[10px] text-slate-500 leading-snug">{it.message}</div>}
      </div>
      <span className={`text-[10px] font-bold uppercase tracking-wider ${m.cls}`} data-testid={`upload-status-state-${it.id}`}>{m.label}</span>
    </li>
  );
};

export default function UploadStatusPanel() {
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => subscribeUploads(setItems), []);
  if (!items.length) return null;

  const count = (s) => items.filter((i) => i.status === s).length;
  const active = count("uploading") + count("waiting");
  const summary = `${count("done")} done · ${count("uploading")} uploading · ${count("waiting")} waiting${count("failed") ? ` · ${count("failed")} failed` : ""}`;

  return (
    <div
      className="fixed bottom-20 md:bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-slate-700 bg-slate-950/95 backdrop-blur shadow-2xl"
      role="region"
      aria-label="Photo upload status"
      data-testid="upload-status-panel"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-800">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-100">Photo uploads</div>
          <div className="text-[10px] text-slate-400 font-mono" aria-live="polite" data-testid="upload-status-summary">{summary}</div>
        </div>
        <button type="button" onClick={() => setCollapsed((c) => !c)} className="p-1 text-slate-400 hover:text-slate-100" aria-label={collapsed ? "Expand upload list" : "Collapse upload list"} data-testid="upload-status-toggle">
          {collapsed ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {active === 0 && (
          <button type="button" onClick={clearFinished} className="p-1 text-slate-400 hover:text-slate-100" aria-label="Close upload list" data-testid="upload-status-close">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {!collapsed && (
        <ul className="max-h-64 overflow-y-auto px-3" data-testid="upload-status-list">
          {items.map((it) => <Row key={it.id} it={it} />)}
        </ul>
      )}
    </div>
  );
}
